import React from 'react';

import { LocalDate } from '@js-joda/core';
import { Box, Flex, Image, List, ListItem, Text } from '@opendoor/bricks/core';
import { colors } from '@opendoor/bricks/theme/eero';

import { LayoutContent } from 'platform/components/layout/Layout';
import { LegacyExternalLink, LegacyExternalLinkProps } from 'platform/remix-run/react';

import {
  BROKERAGE_SERVICES_URL,
  CCPA_URL,
  OPENDOOR_BROKERAGE_URL,
  PRIVACY_CENTER_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from 'shared/helpers/routes';

import { useMlsComplianceContext } from 'buying/MlsComplianceContext';

const Separator: React.FC = () => <Box height="12px" width="1px" bgColor="neutrals90" mx={2} />;

const Trademark: React.FC = () => (
  <Flex alignItems="center">
    <Text size="s00" color="neutrals90" data-testid="trademark">
      &copy; {LocalDate.now().year()} Opendoor
    </Text>
    <Separator />
    <Text size="s00" color="neutrals90">
      All Rights Reserved
    </Text>
  </Flex>
);

interface FooterLinkProps extends Omit<LegacyExternalLinkProps, 'aria-label'> {
  displayName: string;
}

const FooterLink: React.FC<FooterLinkProps> = ({ analyticsName, displayName, href, ...props }) => (
  <LegacyExternalLink
    analyticsName={analyticsName}
    href={href}
    target="_blank"
    textDecoration="underline"
    textDecorationColor="neutrals90"
    fontSize="s00"
    lineHeight="s00"
    color="neutrals90"
    aria-label={null}
    {...props}
  >
    {displayName}
  </LegacyExternalLink>
);

const FinePrintLink: React.FC<FooterLinkProps> = (props) => (
  <FooterLink mx={2} fontSize="s000" lineHeight="s000" {...props} />
);

const FinePrint = () => (
  <List variant="text" fontSize="s000" color="neutrals90">
    <ListItem>
      Learn more about
      <FinePrintLink
        displayName="Opendoor Brokerage"
        analyticsName="opendoor-brokerage"
        href={OPENDOOR_BROKERAGE_URL}
      />
    </ListItem>
    <br />
    <ListItem>
      Opendoor Brokerage LLC and Opendoor Brokerage Inc. holds real estate brokerage
      <FinePrintLink
        displayName="licenses"
        analyticsName="opendoor-brokerage-licenses"
        href={OPENDOOR_BROKERAGE_URL}
      />
      in multiple states.
    </ListItem>
    <br />
    <ListItem>Opendoor Brokerage Inc. California DRE #02061130</ListItem>
    <br />
    <ListItem>
      <FinePrintLink
        displayName="§ 442-H New York Standard Operating Procedures"
        analyticsName="ny-sop"
        href="https://s3.amazonaws.com/odfil.es/static/documents/NY+Operating+Procedures.pdf"
      />
    </ListItem>
    <ListItem>
      <FinePrintLink
        displayName="§ New York Fair Housing Notice"
        analyticsName="ny-fhn"
        href="https://s3.amazonaws.com/odfil.es/static/documents/fair-housing-notice.pdf"
      />
    </ListItem>
    <ListItem>
      <List variant="text" display="flex" alignItems="center">
        <ListItem>TREC: </ListItem>
        <ListItem>
          <FinePrintLink
            displayName="Information about brokerage services"
            analyticsName="brokerage-services"
            href={BROKERAGE_SERVICES_URL}
          />
        </ListItem>
        <ListItem>
          <Separator />
        </ListItem>
        <ListItem>
          <FinePrintLink
            displayName="Consumer Protection Notice"
            analyticsName="ccpa"
            href={CCPA_URL}
          />
        </ListItem>
      </List>
    </ListItem>
    <br />
    <ListItem>
      <FinePrintLink
        displayName="Contact Opendoor Brokerage LLC and Opendoor Brokerage Inc"
        analyticsName="contact-us"
        href="https://www.opendoor.com/contact-us"
      />
    </ListItem>
  </List>
);

const LegalLinks: React.FC = () => (
  <List variant="text" display="flex" flexWrap="wrap" alignItems="center" mb={5}>
    <ListItem>
      <FooterLink
        analyticsName="terms-of-service"
        displayName="Terms of Service"
        href={TERMS_OF_SERVICE_URL}
      />
    </ListItem>
    <ListItem>
      <Separator />
    </ListItem>
    <ListItem>
      <FooterLink
        analyticsName="privacy-policy"
        displayName="Privacy Policy"
        href={PRIVACY_POLICY_URL}
      />
    </ListItem>
    <ListItem>
      <Separator />
    </ListItem>
    <ListItem>
      <FooterLink
        analyticsName="do-not-sell-my-info"
        displayName="Do Not Sell My Info"
        href={PRIVACY_CENTER_URL}
      />
    </ListItem>
  </List>
);

interface MlsLogosProps {
  logoUrls: string[];
}

const MlsLogos: React.FC<MlsLogosProps> = ({ logoUrls }) => (
  <Flex data-testid="mls-logos" flexWrap="wrap" alignItems="center" mb={5}>
    *
    {logoUrls.map((url) => (
      <Image key={url} src={url} alt="" height="25px" mr={5} />
    ))}
  </Flex>
);

const MlsLegalText: React.FC = () => (
  <Text color="neutrals90" size="s000" maxWidth={['100%', null, '760px']}>
    Data provided by the Multiple Listing Service (MLS) is solely for personal, not commercial, use
    and is subject to copyright. All property information should be verified by the recipient and
    none is guaranteed as accurate.
  </Text>
);

interface FooterProps {
  hideSeparator?: boolean;
  disclaimer?: React.ReactNode;
}

/**
 * The footer that appears on all pages.
 */
export const Footer: React.FC<FooterProps> = ({ hideSeparator, disclaimer }) => {
  const { mlsLogoUrls, showMlsLegalText } = useMlsComplianceContext();
  const showMlsLogos = mlsLogoUrls.length > 0;

  return (
    <LayoutContent
      flexDir={['column', null, 'row']}
      data-testid="global-footer"
      width="100%"
      justifyContent="space-between"
      borderTop={!hideSeparator ? `2px solid ${colors.neutrals20}` : undefined}
    >
      <Box mb={5}>
        <Trademark />
        <LegalLinks />
        <FinePrint />
        {disclaimer && (
          <Text
            maxWidth={['100%', null, '540px', '720px']}
            size="s000"
            color="neutrals90"
            data-testid="disclaimer"
            mt={5}
          >
            {disclaimer}
          </Text>
        )}
        {showMlsLegalText && (
          <Box mt={5}>
            {showMlsLogos && <MlsLogos logoUrls={mlsLogoUrls} />}
            <MlsLegalText />
          </Box>
        )}
      </Box>
      <Box>
        <Image
          src="https://imgdrop.imgix.net/d0677f541b294b4f9de00719940717b3.svg"
          alt="Equal housing opportunity"
          w="36px"
          h="35px"
          lineHeight="100"
        />
      </Box>
    </LayoutContent>
  );
};
